import { track } from "~/lib/track";

const STORAGE_KEY = "lekkerladen:overstapper-v1";
/** Survive email OTP and a reload; drop after a week so stale tabs cannot claim forever. */
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** utm_campaign value that marks a customer switching from another provider. */
const OVERSTAPPER_CAMPAIGN = "overstap";

type StoredFlag = { capturedAt: number; source?: string };

const log = (event: string, detail: Record<string, unknown> = {}) => {
  console.info(JSON.stringify({ event, ...detail }));
};

const readStore = (): StoredFlag | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredFlag;
    if (typeof parsed.capturedAt !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeStore = (value: StoredFlag): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    log("overstapper_hook_storage_failed", { reason: "write" });
  }
};

const clearStore = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/** True when utm_campaign marks an overstapper, e.g. from the Framer MID checker. */
export function isOverstapperCampaign(value: unknown): boolean {
  return typeof value === "string" && value.trim().toLowerCase() === OVERSTAPPER_CAMPAIGN;
}

const SOURCE_SLUG = /^[a-z0-9_-]{1,64}$/;

/**
 * utm_source as the lowercase slug the API accepts, or undefined. The router parses
 * `utm_source=123` into a number, so numbers count. A bad source is dropped, never sent:
 * the API would reject the whole claim and the overstapper would lose the hook.
 */
export function normaliseUtmSource(value: unknown): string | undefined {
  if (typeof value !== "string" && typeof value !== "number") return undefined;

  const slug = String(value).trim().toLowerCase();
  if (SOURCE_SLUG.test(slug)) return slug;

  log("overstapper_hook_source_dropped", { reason: "invalid" });
  return undefined;
}

/**
 * Persist the overstapper flag (and the utm_source it came from) from the entry URL so
 * it survives register and reload. Safe to call repeatedly: a flag that is already
 * pending is kept as it is, so a reload does not reset its expiry or repeat the event.
 */
export function captureOverstapperFromSearch(search: Record<string, unknown>): void {
  if (typeof window === "undefined") return;

  if (!isOverstapperCampaign(search.utm_campaign)) {
    return;
  }

  if (pendingFlag()) return;

  const source = normaliseUtmSource(search.utm_source);
  writeStore({ capturedAt: Date.now(), source });
  log("overstapper_hook_captured", { source });
  track("overstapper_captured");
}

/** Pending, non-expired local flag waiting to be claimed on the API. */
function pendingFlag(now: number = Date.now()): StoredFlag | null {
  if (typeof window === "undefined") return null;

  const stored = readStore();
  if (!stored) return null;

  if (now - stored.capturedAt > TTL_MS) {
    clearStore();
    log("overstapper_hook_dropped", { reason: "expired" });
    return null;
  }

  return stored;
}

export function peekOverstapperFlag(now: number = Date.now()): boolean {
  return pendingFlag(now) !== null;
}

export function clearOverstapperFlag(): void {
  clearStore();
}

/** Test helper: reset storage between cases. */
export function resetOverstapperFlagForTests(): void {
  clearStore();
}

/**
 * If a pending local flag exists, claim it on the API once and clear storage.
 * No-op when nothing is pending; failures leave the flag so a later retry can claim.
 */
export async function claimOverstapperHookIfNeeded(
  claim: (source?: string) => Promise<{ stored: boolean; alreadyStored: boolean }>,
): Promise<void> {
  const flag = pendingFlag();
  if (!flag) return;

  try {
    const result = await claim(flag.source);
    clearStore();
    log("overstapper_hook_claimed", {
      stored: result.stored,
      alreadyStored: result.alreadyStored,
    });
    if (result.stored) track("overstapper_claimed");
  } catch (error) {
    log("overstapper_hook_claim_failed", {
      message: error instanceof Error ? error.message : String(error),
    });
  }
}
